import {
  Button,
  FormField,
  Select,
  SelectProps,
  SpaceBetween,
} from '@cloudscape-design/components';
import { useEffect, useState, useRef, memo } from 'react';
import AuditTypeSelect from '../audit/AuditTypeSelect';
import { useDivision } from 'src/store/division/useDivision';
import useUserProfile from 'src/hooks/useUserProfile';
import SingleSelectDivision from '../division/SingleSelectDivision';
import { Division } from 'src/types/orbit/Division';

export interface SearchHomeRequest {
  auditTypes?: string[];
  divisions?: string[];
}

interface HomeFilterProps {
  defaultSearchParams: SearchHomeRequest;
  type: 'task' | 'audit';
  onSearchChange: (searchParams: SearchHomeRequest) => void;
}

const HomeFilter = ({ onSearchChange, defaultSearchParams, type }: HomeFilterProps) => {
  const { getUserBusinessUnitId, getUserProgramId, currentUser } = useUserProfile();
  const { divisionList, fetcDivisionsApiStatus, loadDivisionsByBusinessUnit } =
    useDivision();

  const [searchParams, setSearchParams] =
    useState<SearchHomeRequest>(defaultSearchParams);

  useEffect(() => {
    if (getUserBusinessUnitId()) {
      loadDivisionsByBusinessUnit({
        businessUnitId: getUserBusinessUnitId(),
        programId: getUserProgramId(),
        isDeleted: false,
        isActive: true,
      });
    }
  }, [currentUser]);

  return (
    <SpaceBetween data-testid="home-filter-container" size="l">
      {type === 'audit' && (
        <FormField data-testid="audit-type-field" label="Audit Type">
          <AuditTypeSelect
            data-testid="audit-type-select"
            selectedAuditType={searchParams.auditTypes?.[0] ?? ''}
            onSelectionChange={(auditType: string) => {
              const updatedParams = {
                ...searchParams,
                auditTypes: [auditType],
              };
              setSearchParams(updatedParams);
              onSearchChange(updatedParams);
            }}
          />
        </FormField>
      )}
      <FormField label="Division" data-testid="division-field">
        <SingleSelectDivision
          data-testid="division-select"
          divisions={divisionList}
          isLoading={fetcDivisionsApiStatus.isLoading}
          error={fetcDivisionsApiStatus.errorMessage}
          selectedDivision={searchParams.divisions ? searchParams.divisions[0] : null}
          onItemSelection={(item: Division | null) => {
            const updatedParams = {
              ...searchParams,
              divisions: item?.divisionName ? [item?.divisionName] : undefined,
            };
            setSearchParams(updatedParams);
            onSearchChange(updatedParams);
          }}
        />
      </FormField>

      <Button
        data-testid="reset-button"
        onClick={() => {
          const resetParams = {};
          setSearchParams(resetParams);
          onSearchChange(resetParams);
        }}
      >
        Reset
      </Button>
    </SpaceBetween>
  );
};

export default memo(
  HomeFilter,
  (prev, next) =>
    prev.onSearchChange === next.onSearchChange &&
    prev.defaultSearchParams === next.defaultSearchParams,
);
